import { parseIsoMaybe } from '@heidi/utils/dates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type {
  DocumentsAndJobsResponse,
  JobListItem,
  LibraryItem,
  UserDocumentItem,
} from '../../../../contracts';
import {
  COLLECTIONS_QUERY_KEY,
  COLLECTION_DETAIL_QUERY_KEY,
  DOCUMENTS_AND_JOBS_KEY,
} from '../../../../contracts';
import { useIsAuthenticated } from '../../../injected';
import { listDocumentsAndJobs } from '../api';

interface UseDocumentsAndJobsOptions {
  enabled?: boolean;
  /** Poll interval in ms when there are active jobs (default: 5000) */
  pollWhenActive?: number;
}

/**
 * Convert a job to a unified library item
 */
function jobToLibraryItem(job: JobListItem): LibraryItem {
  return {
    id: job.job_id,
    type: 'job',
    filename: job.filename,
    scope: job.scope,
    team_id: job.team_id,
    status: job.status,
    error_message: job.error_message,
    chunk_count: 0,
    created_at: job.created_at,
    uploaded_by: job.uploaded_by,
    retryable: job.retryable ?? false,
    collection_id: job.collection_id ?? null,
  };
}

/**
 * Convert a document to a unified library item.
 * Documents in the response are always completed (backend only returns completed docs).
 */
function documentToLibraryItem(doc: UserDocumentItem): LibraryItem {
  return {
    id: doc.document_id,
    type: 'document',
    filename: doc.filename,
    scope: doc.scope,
    team_id: doc.team_id,
    // Documents are always completed - the backend only includes them when done
    status: 'completed',
    error_message: null,
    chunk_count: doc.chunk_count,
    created_at: doc.created_at,
    uploaded_by: doc.uploaded_by,
    retryable: false, // Completed documents can't be retried
    // Pass through provenance so the Library doc card / filter pill can
    // branch on it. Legacy backend rows omit the field; we treat that
    // as 'user' implicitly (the Library filter switch defaults that way).
    source: doc.source,
    document_kind: doc.document_kind,
    // Pass through nested metadata
    document_metadata: doc.document_metadata,
  };
}

/**
 * Helper to parse dates from backend (handles missing timezone suffix).
 * Defined at module level to avoid recreation on each render.
 */
const parseJobDate = (dateStr: string): number => {
  const parsed = parseIsoMaybe(dateStr);
  return parsed?.getTime() ?? 0;
};

/**
 * Hook to fetch combined jobs and documents for the Library UI.
 * Automatically polls when there are active (non-completed) jobs.
 *
 * Note: Uses manual polling via useEffect instead of React Query's refetchInterval
 * because refetchInterval has issues with the global 1-hour staleTime config.
 */
export function useDocumentsAndJobs({
  enabled = true,
  pollWhenActive = 5000, // 5 seconds
}: UseDocumentsAndJobsOptions = {}) {
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();
  const prevHadActiveJobsRef = useRef(false);
  const isAuthenticated = useIsAuthenticated();

  const query = useQuery({
    queryKey: [DOCUMENTS_AND_JOBS_KEY],
    queryFn: listDocumentsAndJobs,
    enabled: enabled && isAuthenticated,
    // Override global staleTime to ensure fresh data
    staleTime: 0,
    // Refetch when user returns to tab
    refetchOnWindowFocus: true,
  });

  // Transform to unified items sorted by created_at (newest first)
  const items: LibraryItem[] = query.data
    ? [
        ...query.data.jobs.map(jobToLibraryItem),
        ...query.data.documents.map(documentToLibraryItem),
      ].sort((a, b) => parseJobDate(b.created_at) - parseJobDate(a.created_at))
    : [];

  // Check if there are active jobs that need polling
  const hasActiveJobs = items.some(
    (item) =>
      item.type === 'job' && item.status !== 'completed' && item.status !== 'failed'
  );

  // Check if any active job is recent enough to poll for (5 min max to avoid polling stuck jobs forever)
  const maxPollDuration = 5 * 60 * 1000; // 5 minutes
  const shouldPoll = (() => {
    if (!hasActiveJobs || !query.data) return false;

    const now = Date.now();
    const activeJobs = query.data.jobs.filter(
      (job) => job.status !== 'completed' && job.status !== 'failed'
    );

    // Poll if ANY active job is within the time limit
    return activeJobs.some((job) => {
      const jobAge = now - parseJobDate(job.created_at);
      return jobAge <= maxPollDuration;
    });
  })();

  // Manual polling with useEffect - more reliable than refetchInterval
  const { refetch } = query;
  useEffect(() => {
    // Clear any existing interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Start polling if we should
    if (shouldPoll && enabled) {
      pollingRef.current = setInterval(() => {
        void refetch();
      }, pollWhenActive);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [shouldPoll, enabled, pollWhenActive, refetch]);

  // Invalidate collection queries when jobs finish processing.
  // Backend auto-adds documents to collections when jobs complete,
  // so we need to refresh collection data (including document_count).
  useEffect(() => {
    const prevHadActiveJobs = prevHadActiveJobsRef.current;
    prevHadActiveJobsRef.current = hasActiveJobs;

    if (prevHadActiveJobs && !hasActiveJobs) {
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [COLLECTION_DETAIL_QUERY_KEY],
      });
    }
  }, [hasActiveJobs, queryClient]);

  // Named passthrough rather than `...query`: spreading a query result
  // subscribes every consumer to every field React Query tracks.
  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    refetch,
    items,
    jobs: query.data?.jobs ?? [],
    documents: query.data?.documents ?? [],
    hasActiveJobs,
  };
}

/**
 * Hook to add an optimistic job entry when upload starts
 */
export function useOptimisticUpload() {
  const queryClient = useQueryClient();

  /**
   * Add an optimistic job entry to the cache.
   * Uses client_upload_id for deterministic reconciliation with server data.
   */
  const addOptimisticJob = (
    filename: string,
    scope: 'user' | 'team',
    team_id: string | null,
    clientUploadId: string,
    collectionId?: string | null
  ) => {
    const optimisticId = `optimistic-${clientUploadId}`;

    const optimisticEntry: JobListItem = {
      job_id: optimisticId,
      filename,
      scope,
      team_id,
      status: 'pending' as const,
      error_message: null,
      uploaded_by: 'me',
      created_at: new Date().toISOString(),
      retryable: false,
      client_upload_id: clientUploadId,
      // The collection page matches in-flight uploads on this field. Without it
      // the card stays hidden until a poll returns the server's copy of the job,
      // which a fast upload finishes before (EVI-2437).
      collection_id: collectionId ?? null,
    };

    queryClient.setQueryData<DocumentsAndJobsResponse>(
      [DOCUMENTS_AND_JOBS_KEY],
      (old) => {
        if (!old) {
          return { jobs: [optimisticEntry], documents: [] };
        }
        return { ...old, jobs: [optimisticEntry, ...old.jobs] };
      }
    );

    return optimisticId;
  };

  /**
   * Reconcile the optimistic entry with the real server response.
   * Uses client_upload_id for matching — if the server already has a job
   * with this client_upload_id (from polling/refetch), drops the optimistic entry.
   * Otherwise, updates the optimistic entry with the real job_id.
   */
  const updateOptimisticJob = (
    optimisticId: string,
    realJobId: string,
    clientUploadId?: string
  ) => {
    queryClient.setQueryData<DocumentsAndJobsResponse>(
      [DOCUMENTS_AND_JOBS_KEY],
      (old) => {
        if (!old) return old;

        // Prefer client_upload_id matching (deterministic), fall back to job_id matching
        const serverEntryExists = clientUploadId
          ? old.jobs.some(
              (job) =>
                job.client_upload_id === clientUploadId && job.job_id !== optimisticId
            )
          : old.jobs.some((job) => job.job_id === realJobId);

        if (serverEntryExists) {
          // Server already has this job — drop the optimistic entry
          return {
            ...old,
            jobs: old.jobs.filter((job) => job.job_id !== optimisticId),
          };
        }

        // No server entry yet — update optimistic with real job_id
        return {
          ...old,
          jobs: old.jobs.map((job) =>
            job.job_id === optimisticId ? { ...job, job_id: realJobId } : job
          ),
        };
      }
    );
  };

  /**
   * Remove an optimistic job (e.g., on error)
   */
  const removeOptimisticJob = (optimisticId: string) => {
    queryClient.setQueryData<DocumentsAndJobsResponse>(
      [DOCUMENTS_AND_JOBS_KEY],
      (old) => {
        if (!old) return old;

        return {
          ...old,
          jobs: old.jobs.filter((job) => job.job_id !== optimisticId),
        };
      }
    );
  };

  return {
    addOptimisticJob,
    updateOptimisticJob,
    removeOptimisticJob,
  };
}
